import { store } from '../store/store';
import { logout } from '../store/slices/authSlice';
import { addNotification } from '../store/slices/notificationSlice';
import { updateContactData } from '../store/slices/profileSlice';
import { UserService } from '../api/generated/services/UserService';

// Store original fetch
const originalFetch = window.fetch;

// Flag to prevent logout loop
let isLoggedOut = false;

// Logout helper function
const handleLogout = () => {
  if (!isLoggedOut) {
    isLoggedOut = true;
    store.dispatch(logout());
  }
};

// Reset logout flag (call this after successful login)
export const resetLogoutFlag = () => {
  isLoggedOut = false;
};

// Deduplicate the subuser-permission notification: pages like the dashboard
// fire several parallel requests that can all answer the same 403.
let lastPermissionDeniedMessage = '';
let lastPermissionDeniedAt = 0;
const PERMISSION_DENIED_DEDUPE_MS = 3000;

/**
 * 403 `subuser_permission_denied`: show the backend's `message` (it already
 * names the missing permission) and silently re-fetch the user - the owner
 * may have revoked permissions after this subuser logged in. No logout, no
 * redirect: the user stays on the page.
 */
const handleSubuserPermissionDenied = (message: unknown) => {
  const text =
    typeof message === 'string' && message.trim()
      ? message
      : 'Your account does not have permission for this action. Ask the account owner to grant it.';
  const now = Date.now();
  if (
    text === lastPermissionDeniedMessage &&
    now - lastPermissionDeniedAt < PERMISSION_DENIED_DEDUPE_MS
  ) {
    return;
  }
  lastPermissionDeniedMessage = text;
  lastPermissionDeniedAt = now;
  store.dispatch(addNotification({ type: 'error', message: text }));
  UserService.getAppUserGet()
    .then((user) => store.dispatch(updateContactData(user)))
    .catch(() => {
      // Best-effort refresh only - the notification was already shown.
    });
};

// Public URLs that don't require authentication
const PUBLIC_URLS = [
  '/auth/login',
  '/auth/register',
  // Add other public endpoints here
];

// Override fetch with our interceptor
window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  try {
    // If already logged out, don't try to make requests
    if (isLoggedOut) {
      console.log('Session expired. Please login again.');
      throw new Error('Session expired. Please login again.');
    }

    const url = input instanceof Request ? input.url : input.toString();
    const isPublicUrl = PUBLIC_URLS.some(publicUrl => url.includes(publicUrl));
    const headers = new Headers(init?.headers);
    
    if (!isPublicUrl) {
      const token = localStorage.getItem('token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    const response = await originalFetch(input, { ...init, headers });
    
    // Log all errors regardless of content type
    if (!response.ok) {
      const method = input instanceof Request ? input.method : init?.method || 'GET';

      // Clone response because we need to read it twice
      const clone = response.clone();
      
      // Try to get error details
      let errorData;
      try {
        errorData = await clone.json();
      } catch {
        try {
          errorData = await response.clone().text();
        } catch {
          errorData = response.statusText;
        }
      }

      console.error('API Error:', {
        url,
        method,
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });

      // Handle 401 Unauthorized - logout user
      if (response.status === 401 && !isPublicUrl) {
        console.log('Unauthorized - logging out');
        handleLogout();
      }

      // 403 subuser permission errors (NOT license_required - that 403 has
      // its own handling in the feature slices).
      if (
        response.status === 403 &&
        errorData &&
        typeof errorData === 'object' &&
        (errorData as any).error === 'subuser_permission_denied'
      ) {
        handleSubuserPermissionDenied((errorData as any).message);
      }
    }

    return response;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
};
